import React from 'react';
import { Menu, MenuButton, MenuItem, MenuItems, MenuSeparator } from '@headlessui/react';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { cx, focusRing } from './cx';

export type ActionMenuItem =
  | {
      label: string;
      onClick: () => void;
      icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
      danger?: boolean;
      disabled?: boolean;
    }
  | 'separator';

export interface ActionMenuProps {
  /** What the trigger opens, e.g. "More actions for Clean gutters". */
  label: string;
  items: ActionMenuItem[];
  /** Replaces the ⋯ trigger; receives nothing, so render a plain element (the MenuButton wraps it). */
  trigger?: React.ReactNode;
  /** Replaces the trigger's own look (an 8×8 icon button), e.g. for an avatar or a tab. */
  triggerClassName?: string;
  /** Shown above the items and not selectable, e.g. who is signed in. */
  header?: React.ReactNode;
  /** Where the list opens; 'top end' for a trigger at the bottom of the screen. */
  anchor?: 'bottom end' | 'bottom start' | 'top end' | 'top start';
  className?: string;
}

/** A ⋯ button that opens a list of actions, with Delete and the like set apart in red. */
export function ActionMenu({ label, items, trigger, triggerClassName, header, anchor = 'bottom end', className }: ActionMenuProps) {
  return (
    <Menu as="div" className={cx('relative', className)}>
      <MenuButton
        aria-label={label}
        title={label}
        className={cx(
          triggerClassName ??
            'inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 data-[open]:bg-gray-100 data-[open]:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100 dark:data-[open]:bg-gray-800 dark:data-[open]:text-gray-100',
          focusRing,
        )}
      >
        {trigger ?? <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />}
      </MenuButton>
      <MenuItems
        anchor={anchor}
        transition
        className={cx(
          'z-50 min-w-52 rounded-xl border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-800 dark:bg-gray-900 dark:shadow-none [--anchor-gap:6px] focus:outline-none',
          anchor.startsWith('top') ? 'origin-bottom-right' : 'origin-top-right',
          'transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 motion-reduce:transition-none',
        )}
      >
        {header && <div className="mb-1 border-b border-gray-100 dark:border-gray-800 px-2.5 pb-2 pt-1.5">{header}</div>}
        {items.map((item, i) =>
          item === 'separator' ? (
            <MenuSeparator key={`sep-${i}`} className="my-1 h-px bg-gray-100 dark:bg-gray-800" />
          ) : (
            <MenuItem key={item.label} disabled={item.disabled}>
              <button
                type="button"
                onClick={item.onClick}
                className={cx(
                  'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm data-[focus]:bg-gray-100 dark:data-[focus]:bg-gray-800 data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
                  item.danger ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100',
                )}
              >
                {item.icon && (
                  <item.icon className={cx('h-4 w-4 flex-none', item.danger ? 'text-red-500 dark:text-red-400' : 'text-gray-400 dark:text-gray-500')} aria-hidden="true" />
                )}
                {item.label}
              </button>
            </MenuItem>
          ),
        )}
      </MenuItems>
    </Menu>
  );
}
